/**
 * Raymarched sun shafts. Half-res, blue-noise dithered stepping, sampled
 * against the same cascade atlas the surface shading uses so the shafts line up
 * exactly with the geometry that casts them (window mullions, alley walls).
 *
 * The result is published on `ctx.lighting.volumetricTexture` for the render
 * team to composite; a self-composite additive quad is used only until they do.
 *
 * BUFFER CONTRACT (this is the hand-off that was mis-scaled — see FINDINGS F1):
 *
 *   rgb   in-scattered radiance along the view ray, in *scene units*
 *         (1.0 = SCENE_UNIT_NITS nits — see Sky.js). Added to the frame.
 *   a     TRANSMITTANCE of the medium along the view ray, 1 = perfectly clear.
 *         The frame is MULTIPLIED by it.
 *
 * The compositor in src/render/Composite.js blends
 *   dst = src.rgb + dst * src.a
 * so `a` must be transmittance, not opacity. It previously wrote `1 - trans`,
 * which multiplied the whole world by ~0.005 while leaving the viewmodel (drawn
 * after the composite) untouched — the world went black, the gun clipped white,
 * and auto-exposure lifted ~2 stops trying to reconcile the two.
 *
 * Owner: lighting.
 */

export const VOL_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4( position.xy * 2.0, 0.0, 1.0 );
}
`;

export const VOL_FRAG = /* glsl */ `
precision highp float;

varying vec2 vUv;

uniform sampler2D uDepth;
uniform float uHwDepth;          // 1 = uDepth is a hardware depth texture
uniform float uReversedDepth;
uniform sampler2D uBlueNoise;
uniform vec2  uNoiseScale;
uniform float uFrameJitter;

uniform mat4  uInvProjection;
uniform mat4  uInvView;
uniform vec3  uCameraPos;
uniform vec2  uCameraRange;      // near, far

uniform sampler2D aaaShadowAtlas;
uniform mat4  aaaCascadeMatrix[ AAA_CASCADES ];
uniform vec4  aaaCascadeInfo[ AAA_CASCADES ];
uniform vec4  aaaShadowParams;

uniform vec3  uSunDirection;
uniform vec3  uSunIlluminance;   // lx, perpendicular
uniform vec3  uAmbientRadiance;  // nits, isotropic term
uniform vec4  uVolParams;        // x: density, y: 1/scaleHeight, z: base height, w: anisotropy
uniform float uScatter;          // scattering coefficient, 1/m
uniform float uExtinction;
uniform float uMaxDistance;
uniform int   uSteps;

// Indoor dust. See mediumDensity().
uniform sampler3D aaaIvSky;
uniform vec3  aaaIvMin;
uniform vec3  aaaIvInvSize;
uniform vec3  aaaIvTexelInset;
uniform float uIndoorDust;

const vec4 AAA_UNPACK = vec4( ( 255.0 / 256.0 ) / vec3( 1.0, 256.0, 65536.0 ), 1.0 / 16777216.0 );

float unpackD( vec4 v ) { return dot( v, AAA_UNPACK ); }

float sampleAtlas( vec2 uv, float tile ) {
  float tpr = aaaShadowParams.x;
  vec2 org = vec2( mod( tile, tpr ), floor( tile / tpr ) );
  vec2 auv = ( org + clamp( uv, vec2( 0.002 ), vec2( 0.998 ) ) ) / tpr;
  return unpackD( texture2D( aaaShadowAtlas, auv ) );
}

float sunVisibility( vec3 worldPos, float viewDepth ) {
  int c = AAA_CASCADES - 1;
  for ( int i = 0; i < AAA_CASCADES; i ++ ) {
    if ( viewDepth < aaaCascadeInfo[ i ].x ) { c = i; break; }
  }
  vec4 info = aaaCascadeInfo[ c ];
  vec4 sc = aaaCascadeMatrix[ c ] * vec4( worldPos, 1.0 );
  vec3 uvz = sc.xyz / sc.w;
  if ( uvz.x < 0.0 || uvz.x > 1.0 || uvz.y < 0.0 || uvz.y > 1.0 || uvz.z > 1.0 || uvz.z < 0.0 ) return 1.0;
  float bias = 0.35 / info.z;      // 35 cm — volumetrics want a generous bias
  float d = sampleAtlas( uvz.xy, info.w );
  return step( uvz.z - bias, d );
}

float phaseHG( float mu, float g ) {
  float g2 = g * g;
  float d = 1.0 + g2 - 2.0 * g * mu;
  return ( 1.0 - g2 ) / ( 4.0 * 3.141592653589793 * max( 1e-3, d * sqrt( max( 1e-3, d ) ) ) );
}

/**
 * Density of the scattering medium at p.
 *
 * Outdoor air is nearly clean — sigma_s ~ 4e-5 /m — and a beam in it is
 * invisible over the 65 m this march covers. That is physically right and it is
 * why light_shafts had no shafts at all: the whole shot's subject was being
 * rendered at ~1 % of the frame's mid-tone. What makes a real god ray is *dust*,
 * and dust lives indoors, where nothing sweeps it out: a lived-in room runs one
 * to two orders of magnitude denser than the street outside its window.
 *
 * So the medium is scaled by how much open sky the point can see, read from the
 * irradiance volume's visibility channel — the same term the surface shader uses
 * to decide an interior is an interior. Outdoors (vis ~ 1) the density is
 * unchanged and nothing is veiled; inside a room (vis < 0.1) it climbs by
 * uIndoorDust and the shaft through the window becomes the subject it is
 * supposed to be.
 */
float mediumDensity( vec3 p, out float skyVis ) {
  float base = uVolParams.x * exp( -max( 0.0, p.y - uVolParams.z ) * uVolParams.y );
  vec3 uvw = ( p - aaaIvMin ) * aaaIvInvSize;
  // Outside the volume there is no interior to be inside of.
  if ( any( lessThan( uvw, vec3( 0.0 ) ) ) || any( greaterThan( uvw, vec3( 1.0 ) ) ) ) { skyVis = 1.0; return base; }
  vec3 lo = aaaIvTexelInset;
  vec3 hi = vec3( 1.0 ) - lo;
  float vHere = texture( aaaIvSky, clamp( uvw, lo, hi ) ).a;
  // Second tap half a cell lower. A trilinear read at head height still blends
  // upward toward the layer above the roof, and that leak is what told the
  // dust it was outdoors while standing in a hall. The lower tap is always
  // deeper inside the room, and outdoors both taps read ~1 so nothing changes.
  float vBelow = texture( aaaIvSky, clamp( uvw - vec3( 0.0, aaaIvTexelInset.y, 0.0 ), lo, hi ) ).a;
  skyVis = min( vHere, vBelow );
  // NB: smoothstep with edge0 > edge1 is undefined in GLSL and returns 0 on
  // ANGLE/Metal — the same trap that once zeroed the whole cloud layer
  // (see clouds.glsl.js :: aaaCloudProfile). Invert explicitly.
  //
  // The ramp is deliberately tight. A street canyon reads vis ~ 0.17 and a
  // room reads vis ~ 0.01-0.03, so anything wider than this put the dust in
  // the alleys as well and the result was a milky wash on the walls rather
  // than a beam in the air.
  // Measured: a room reads 0.007-0.022, an open alley 0.12-0.27, under a bridge
  // or an arcade 0.04-0.08. This ramp puts full dust in the room, a fraction
  // under a covered passage — which is right — and none in the open lane. A
  // wider ramp put ~180x density in the whole alley and re-created exactly the
  // milky veil of FINDINGS F1.
  float indoors = 1.0 - smoothstep( 0.01, 0.09, skyVis );
  return base * mix( 1.0, uIndoorDust, indoors );
}

void main() {
  float near = uCameraRange.x;
  float far = uCameraRange.y;

  vec4 ndc = vec4( vUv * 2.0 - 1.0, 1.0, 1.0 );
  vec4 vpos = uInvProjection * ndc;
  vpos /= vpos.w;
  vec3 viewRay = vpos.xyz;
  float invCos = length( viewRay ) / max( 1e-4, -viewRay.z );
  vec3 worldDir = normalize( ( uInvView * vec4( viewRay, 0.0 ) ).xyz );

  float packed = uHwDepth > 0.5 ? texture2D( uDepth, vUv ).r : unpackD( texture2D( uDepth, vUv ) );
  if ( uReversedDepth > 0.5 ) packed = 1.0 - packed;
  float sceneDist = uMaxDistance;
  if ( packed < 0.9999 ) {
    float viewZ = ( near * far ) / ( ( far - near ) * packed - far );
    sceneDist = min( -viewZ * invCos, uMaxDistance );
  }

  float noise = texture2D( uBlueNoise, vUv * uNoiseScale + uFrameJitter ).r;

  int steps = uSteps;
  float dt = sceneDist / float( steps );
  float mu = dot( worldDir, uSunDirection );
  float phase = phaseHG( mu, uVolParams.w );

  vec3 accum = vec3( 0.0 );
  float trans = 1.0;

  for ( int i = 0; i < 64; i ++ ) {
    if ( i >= steps || trans < 0.02 ) break;
    float t = ( float( i ) + noise ) * dt;
    vec3 p = uCameraPos + worldDir * t;

    float skyVis;
    float d = mediumDensity( p, skyVis );
    if ( d > 1e-6 ) {
      float viewDepth = t / max( 1e-4, invCos );
      float vis = sunVisibility( p, viewDepth );
      // The isotropic term is skylight, so it has to be occluded like skylight.
      // Without this the indoor dust boost lit every room with a uniform milky
      // glow — the exact veil FINDINGS F1 spent a session removing.
      vec3 inScatter = uSunIlluminance * ( phase * vis ) + uAmbientRadiance * ( 0.04 + 0.96 * skyVis );
      float sigmaS = uScatter * d;
      float sigmaT = uExtinction * d;
      float stepT = exp( -sigmaT * dt );
      accum += inScatter * sigmaS * ( 1.0 - stepT ) / max( 1e-6, sigmaT ) * trans;
      trans *= stepT;
    }
  }

  // rgb = in-scattering (added), a = transmittance (multiplied). See the header.
  gl_FragColor = vec4( accum, trans );
}
`;

export const VOL_COMPOSITE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uVolumetric;
uniform vec2 uTexel;
uniform float uIntensity;

void main() {
  // 4-tap tent upsample: half-res shafts should not show their pixel grid.
  vec3 c = texture2D( uVolumetric, vUv ).rgb * 0.4;
  c += texture2D( uVolumetric, vUv + vec2(  uTexel.x,  uTexel.y ) ).rgb * 0.15;
  c += texture2D( uVolumetric, vUv + vec2( -uTexel.x,  uTexel.y ) ).rgb * 0.15;
  c += texture2D( uVolumetric, vUv + vec2(  uTexel.x, -uTexel.y ) ).rgb * 0.15;
  c += texture2D( uVolumetric, vUv + vec2( -uTexel.x, -uTexel.y ) ).rgb * 0.15;
  gl_FragColor = vec4( c * uIntensity, 1.0 );
}
`;
